import React from 'react';
import classNames from 'classnames';
import _get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import {
  ColorEnum,
  DepartmentHierarchyNodeFragmentFragment as DepartmentHierarchyNode,
} from 'graphql-common';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import { Values } from '@lib/interfaces/form';
import DepartmentFieldNames from '@lib/enums/fieldNames/departmentFieldNames';
import useDepartmentAncestorChain from '../../utils/useDepartmentAncestorChain';
import DepartmentResultingHierarchyNode from './DepartmentResultingHierarchyNode';
import hierarchyStyles from '../DepartmentHierarchyView/DepartmentHierarchyView.module.scss';

// Placeholder id for the not-yet-saved department being previewed; never
// collides with a real department id, so it's a safe `isCurrent` marker.
const CURRENT_NODE_ID = 'resulting-hierarchy-current';

type Props = {
  values: Values;
};

export default function DepartmentResultingHierarchy({ values }: Props) {
  const { t } = useTranslation();
  const parentId = _get(values, [
    DepartmentFieldNames.ParentDepartment,
    'value',
  ]) as string | undefined;
  const name = (_get(values, DepartmentFieldNames.Name) as string) || '';
  const color = _get(values, [DepartmentFieldNames.Color, 'value']) as
    ColorEnum | undefined;

  const { ancestorChain, loading } = useDepartmentAncestorChain(parentId);

  if (!parentId) return null;

  const currentNode: DepartmentHierarchyNode = {
    id: CURRENT_NODE_ID,
    name: name || t('department-name'),
    color: color || ColorEnum.Gray,
    childrenCount: 0,
  };

  const chain = [...ancestorChain, currentNode];
  const tree = chain.reduceRight<React.ReactNode>(
    (acc, node) => (
      <DepartmentResultingHierarchyNode
        key={node.id}
        node={node}
        isCurrent={node.id === CURRENT_NODE_ID}
      >
        {acc}
      </DepartmentResultingHierarchyNode>
    ),
    null,
  );

  return (
    <div
      className={classNames(
        entityViewStyles.infoItemCol,
        hierarchyStyles.container,
      )}
    >
      <p className={entityViewStyles.infoItemTitle}>
        <MaterialIcon icon="rebase" symbolsOutlined />
        <Typography variant="caption">{t('resulting-hierarchy')}</Typography>
      </p>
      <div className={hierarchyStyles.wrapper}>
        {loading ? (
          <div className={hierarchyStyles.loadingWrapper}>
            <CircleLoader
              size={24}
              padding="8px 0"
              classes={{ root: hierarchyStyles.loadingIconRoot }}
            />
          </div>
        ) : (
          <ul className={hierarchyStyles.tree}>{tree}</ul>
        )}
      </div>
    </div>
  );
}
